import {
  createCommand,
  ScpCommandPayload,
} from "lib/deviceDrivers/truTest/scp/device/commands";
import {
  CommandResponseMode,
  DeviceCommand,
} from "lib/deviceDrivers/sessionFramework/device/types";

const COMMAND_TIMEOUT_MS = 1000;
const MAX_COMMAND_RETRIES = 2;

/**
 * Constructs a device command with the shared defaults for timeout and retry behaviour applied. Individual fields can be overridden for commands that need different retry rules from the norm.
 */
function buildCommand(
  name: string,
  payload: string,
  responseMode: DeviceCommand["responseMode"],
  overrides: Partial<
    Pick<DeviceCommand, "timeoutMs" | "maxRetries" | "retryOnBusy">
  > = {},
): DeviceCommand {
  return createCommand(name, payload, responseMode, {
    timeoutMs: COMMAND_TIMEOUT_MS,
    maxRetries: MAX_COMMAND_RETRIES,
    retryOnBusy: true,
    ...overrides,
  });
}

/**
 * Builds a command that writes the given date string to the device. Time synchronisation is best-effort, so the command is sent once and any failure is left uncorrected rather than retried with a stale value.
 */
export function buildSetDateCommand(date: string): DeviceCommand {
  return buildCommand("setDate", `{SD${date}}`, CommandResponseMode.ACK, {
    maxRetries: 0,
    retryOnBusy: false,
  });
}

/**
 * Builds a command that writes the given time string to the device. Time synchronisation is best-effort, so the command is sent once and any failure is left uncorrected rather than retried with a stale value.
 */
export function buildSetTimeCommand(time: string): DeviceCommand {
  return buildCommand("setTime", `{ST${time}}`, CommandResponseMode.ACK, {
    maxRetries: 0,
    retryOnBusy: false,
  });
}

/**
 * Builds a command that reads the current date from the device. Used before setting the date so the driver can detect and report significant clock drift.
 */
export function buildGetDateCommand(): DeviceCommand {
  return buildCommand(
    "getDate",
    ScpCommandPayload.GET_DATE,
    CommandResponseMode.DATA,
    {
      maxRetries: 0,
      retryOnBusy: false,
    },
  );
}

/**
 * Builds a command that reads the current time from the device. Used before setting the time so the driver can detect and report significant clock drift.
 */
export function buildGetTimeCommand(): DeviceCommand {
  return buildCommand(
    "getTime",
    ScpCommandPayload.GET_TIME,
    CommandResponseMode.DATA,
    {
      maxRetries: 0,
      retryOnBusy: false,
    },
  );
}

/**
 * Turns on ACKs and device error reporting before the driver asks for live scan format data.
 * Those settings make the scanner's later responses predictable enough for the protocol parser to follow.
 */
export function syncHardwareConfigCommands(): DeviceCommand[] {
  return [
    buildCommand(
      "enableAcks",
      ScpCommandPayload.ENABLE_ACKS,
      CommandResponseMode.ACK,
      {
        // Allow extra retries for the initial ACK since the BLE GATT may not be ready at first connection.
        maxRetries: 5,
      },
    ),
    buildCommand(
      "enableErrors",
      ScpCommandPayload.ENABLE_ERRORS,
      CommandResponseMode.ACK,
      { maxRetries: 0 },
    ),
    buildCommand(
      "queryLineEndings",
      ScpCommandPayload.QUERY_LINE_ENDINGS,
      CommandResponseMode.DATA,
      { maxRetries: 0 },
    ),
    buildCommand(
      "requestEidOutputFormat",
      ScpCommandPayload.QUERY_EID_OUTPUT_FORMAT,
      CommandResponseMode.DATA,
    ),
  ];
}
